// SPDX-FileCopyrightText: 2026 Christian Meeßen (GFZ) <christian.meessen@gfz-potsdam.de>
// SPDX-FileCopyrightText: 2026 Helmholtz Centre Potsdam - GFZ German Research Centre for Geosciences
//
// SPDX-License-Identifier: EUPL-1.2

import OpenInNew from '@mui/icons-material/OpenInNew'
import {MouseEventHandler} from 'react'

type HgfButtonStyle = 'primary' | 'secondary' | 'tertiary' | 'destructive'
type HgfButtonColorMode = 'light' | 'dark'
type HgfButtonSize = 'base' | 'small'

type HgfButtonProps = {
  children: React.ReactNode //text, icons, or both
  style: HgfButtonStyle
  colorMode?: HgfButtonColorMode
  size?: HgfButtonSize
  disabled?: boolean
  onClick?: MouseEventHandler
  href?: string
  className?: string
  openInNew?: boolean
}

export default function HgfButton({children, style, colorMode = 'dark', size ='base', disabled = false, href, onClick, className, openInNew = false}: HgfButtonProps) {
  const getStyleClasses = (style: HgfButtonStyle, colorMode: HgfButtonColorMode): string => {
    switch (style) {
      case 'primary':
        return colorMode === 'dark' ? 'text-white bg-secondary hover:text-secondary hover:bg-primary' : 'text-secondary bg-[#05e5ba] hover:bg-primary'
      case 'secondary':
        return colorMode === 'dark' ? 'text-secondary bg-white border-secondary border-2 hover:text-white hover:bg-secondary' : 'text-white border-white border-2 hover:text-secondary hover:bg-white'
      case 'tertiary':
        return colorMode === 'dark' ? 'text-secondary hover:bg-secondary/20' : 'text-primary hover:bg-primary/20'
      case 'destructive':
        return 'text-white bg-error'
    }
  }

  const buttonClasses = `
    flex flex-row items-center justify-center gap-2 w-fit
    ${size === 'base' ? 'px-6 py-4 rounded-full text-2xl' : 'px-4 py-2 rounded-sm text-base'}
    ${disabled ? 'bg-[#CAC9C9] text-[#303030] disabled' : getStyleClasses(style, colorMode)}
    ${className}
  `

  return (
    href ?
      <a
        className={buttonClasses}
        href={href}
        onClick={onClick}
        target={openInNew ? '_blank' : undefined}
      >
        {children}
        {openInNew && <OpenInNew />}
      </a>
      :
      <button
        className={buttonClasses}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
        {openInNew && <OpenInNew />}
      </button>
  )
}
